import React, { useState } from "react";
import { motion } from "framer-motion";

import { color } from "@/lib/theme";

// Palette tokens, not colours — resolved through `color()` at each use site.
// The chain is brand furniture until a step carries a judgement: the finding
// warns, the risk is danger, the retest result passes, and the evidence and
// assurance record at the end are the proof moment.
const STATE_COLOR = {
  attack: "brand",
  system: "brand",
  finding: "warn",
  risk: "danger",
  control: "brand-deep",
  owner: "brand",
  remediation: "brand",
  retest: "brand",
  result: "valid",
  evidence: "gold",
  assurance: "gold",
};

// relation label sits on the connector BEFORE each node (from previous node)
const NODES = [
  { id: "attack", state: "attack", label: "Attack", sub: "Jailbreak attempt · simulated", rel: null,
    fields: [["Type", "Adversarial test"], ["Environment", "Simulated"], ["Vector", "Instruction manipulation"]],
    note: "A controlled adversarial pattern is run against the AI system in a test environment." },
  { id: "system", state: "system", label: "AI System", sub: "Customer Support Agent", rel: "targets",
    fields: [["System", "Customer Support Agent"], ["Ref", "SYS-DEMO-04"], ["Environment", "Simulated"]],
    note: "The demonstration AI system under assessment." },
  { id: "finding", state: "finding", label: "Finding", sub: "Guardrail bypass · HIGH", rel: "produced",
    fields: [["Finding", "FND-DEMO-0042"], ["Severity", "HIGH"], ["Source", "Security Assessment"]],
    note: "The model followed a prohibited instruction pattern during the simulated assessment." },
  { id: "risk", state: "risk", label: "Risk", sub: "Unauthorized model behavior", rel: "maps to",
    fields: [["Risk", "RISK-DEMO-0017"], ["Category", "Behavioral"], ["Status", "Open → mitigated"]],
    note: "The finding maps to a business risk, tracked until a control addresses it." },
  { id: "control", state: "control", label: "Control", sub: "Input / Output Guardrail", rel: "addressed by",
    fields: [["Control", "CTRL-DEMO-A6.2"], ["Maps to", "ISO/IEC 42001 A.6.2"], ["Type", "Preventive"]],
    note: "A guardrail control, mapped to a recognized framework requirement, that reduces the risk." },
  { id: "owner", state: "owner", label: "Owner", sub: "AI Security", rel: "assigned to",
    fields: [["Owner", "AI Security"], ["Accountable", "Yes"], ["SLA", "Tracked"]],
    note: "Accountability is assigned so remediation is owned, not just noted." },
  { id: "remediation", state: "remediation", label: "Remediation", sub: "Guardrail policy updated", rel: "requires",
    fields: [["Action", "Guardrail policy updated"], ["Ref", "REM-DEMO-0033"], ["Status", "Applied"]],
    note: "The recorded remediation action taken to close the risk." },
  { id: "retest", state: "retest", label: "Retest", sub: "Assessment pattern re-executed", rel: "validated by",
    fields: [["Retest", "RTS-DEMO-0091"], ["Pattern", "Original assessment"], ["Residual risk", "Low"]],
    note: "The original adversarial pattern is re-executed to confirm the fix works." },
  { id: "result", state: "result", label: "Result", sub: "PASSED", rel: "produces",
    fields: [["Result", "PASSED"], ["Control effectiveness", "Confirmed"], ["Regression", "None"]],
    note: "The retest passes — the control is demonstrably effective." },
  { id: "evidence", state: "evidence", label: "Evidence", sub: "Evidence package", rel: "captured as",
    fields: [["Evidence", "EV-DEMO-00481"], ["Contains", "Assessment · Finding · Control"], ["Also", "Remediation · Retest · Timestamp"]],
    note: "A dated evidence package links every step — the record behind the claim." },
  { id: "assurance", state: "assurance", label: "Assurance", sub: "Control effectiveness confirmed", rel: "supports",
    fields: [["Outcome", "Assurance updated"], ["Record", "CERT-DEMO-000148"], ["Status", "Verified"]],
    note: "The evidence updates the system’s assurance state — verifiable by others." },
];

const byId = (id) => NODES.find((n) => n.id === id);

export default function SecurityTrace() {
  const [active, setActive] = useState("finding");
  const node = byId(active);
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-start">
      {/* trace */}
      <ol className="relative flex flex-col">
        <span className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-brand/50 via-line to-gold/50" />
        {NODES.map((n, i) => {
          const tone = STATE_COLOR[n.state];
          const isActive = n.id === active;
          return (
            <li key={n.id} className="relative">
              {n.rel && (
                <div className="ml-8 pb-1 pt-1">
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{n.rel}</span>
                </div>
              )}
              <motion.button
                data-testid={`trace-node-${n.id}`}
                onMouseEnter={() => setActive(n.id)}
                onFocus={() => setActive(n.id)}
                onClick={() => setActive(n.id)}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                aria-label={`${n.label}: ${n.sub}`}
                className={`relative flex w-full items-center gap-4 rounded-md border px-4 py-3 text-left transition-colors duration-300 focus:outline-none ${
                  isActive ? "border-line-bright bg-panel-raised" : "border-line bg-panel-raised/70 hover:border-brand/50"
                }`}
              >
                <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border" style={{ borderColor: color(tone, 0.4), background: color("ink") }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: color(tone), boxShadow: isActive ? `0 0 10px ${color(tone, 0.45)}` : `0 0 4px ${color(tone, 0.3)}` }} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">{n.label}</span>
                  <span className="block truncate text-sm text-content">{n.sub}</span>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: color(tone) }}>{n.state === "result" || n.state === "assurance" ? "✓" : ""}</span>
              </motion.button>
            </li>
          );
        })}
      </ol>

      {/* detail panel */}
      <motion.div
        key={active}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-lg border border-line bg-panel-raised p-5 sm:p-7 lg:sticky lg:top-28"
      >
        <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(STATE_COLOR[node.state]) }} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">{node.label}</div>
        <h4 className="mt-2 font-display text-2xl font-medium text-content">{node.sub}</h4>
        <p className="mt-4 text-sm leading-relaxed text-content-muted">{node.note}</p>
        <div className="mt-6 grid gap-px overflow-hidden rounded-md border border-line bg-panel-raised sm:grid-cols-2">
          {node.fields.map(([k, v]) => (
            <div key={k} className="bg-ink p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{k}</div>
              <div className="mt-1 font-mono text-[12px] text-content">{v}</div>
            </div>
          ))}
        </div>
        <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">Simulated data · tap any step to trace</p>
      </motion.div>
    </div>
  );
}
